import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Headphones, Bookmark, BookmarkCheck, Share2, BadgeCheck } from "lucide-react";
import { timeAgo, slugifyName } from "@/lib/api";
import { Article } from "@/lib/types";
import { useLang } from "@/lib/i18n";
import { SECTOR_LABELS } from "@/components/layernav/navConfig";
import { AuroraAvatar } from "@/components/AuthorAvatar";

const UPDATED_MIN_GAP = 45 * 60 * 1000;
const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]/60";

const ROLE_LABELS: Record<string, string> = {
  author: "Contributor · NewsLayer",
  editorial: "NewsLayer Editorial",
  deep_dive: "NewsLayer Research",
  market_brief: "NewsLayer Desk",
};

const catLabel = (c: string) => SECTOR_LABELS[c] || c.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

const fmtPublished = (iso?: string) => {
  if (!iso) return null;
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "UTC" });
  return `${date} at ${time} UTC`;
};

const ActionBtn = ({ onClick, testid, label, children }: {
  onClick: () => void; testid: string; label: string; children: ReactNode;
}) => (
  <button type="button" onClick={onClick} data-testid={testid} aria-label={label} title={label}
    className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-white/[0.1] bg-black/30
      text-xs font-semibold text-gray-400 hover:text-white hover:border-white/25 hover:bg-white/[0.06]
      transition-colors ${FOCUS}`}>
    {children}
  </button>
);

const TS_LABEL: Record<string, string> = { sm: "S", md: "M", lg: "L" };

export const Masthead = ({ article, title, dek, saved, onSave, onShare, hasAudio, onListen, textSize, onCycleTextSize }: {
  article: Article; title: string; dek: string | null; saved: boolean;
  onSave: () => void; onShare: () => void; hasAudio: boolean; onListen: () => void;
  textSize: string; onCycleTextSize: () => void;
}) => {
  const { t } = useLang();
  const cats = (article.categories || []).slice(0, 2);
  const isExternal = ["imported", "pr"].includes(article.content_type || "");
  const isPr = article.content_type === "pr";
  const updatedAt = (article as any).updated_at;
  const showUpdated = !!updatedAt && !!article.published_at &&
    new Date(updatedAt).getTime() - new Date(article.published_at).getTime() > UPDATED_MIN_GAP;
  const role = ROLE_LABELS[article.content_type || ""];
  const liveStateRaw = (article as any).live_state as string | undefined;
  const liveBadge = liveStateRaw && ["breaking", "developing", "live"].includes(liveStateRaw)
    ? liveStateRaw : (!liveStateRaw && article.is_breaking ? "breaking" : null);
  const liveBadgeCls = liveBadge === "developing"
    ? "text-black bg-[#F59E0B]/90 border border-[#F59E0B]/50"
    : "text-white bg-[#B91C1C]/80 border border-[#EF4444]/40";
  const srcName = (article.source_name || "").trim();
  const authName = (article.author_name || "").trim();
  const externalBold = authName && authName.toLowerCase() !== srcName.toLowerCase() ? authName : srcName || authName;

  return (
    <header data-testid="article-masthead">
      {/* Breadcrumb (§7) — quiet navigational context */}
      <nav aria-label="Breadcrumb" data-testid="article-breadcrumb"
        className="flex items-center gap-1.5 font-mono text-[10px] text-gray-600">
        <Link to="/" className={`hover:text-gray-300 transition-colors rounded ${FOCUS}`}>Home</Link>
        {cats.map((c) => (
          <span key={c} className="flex items-center gap-1.5 min-w-0">
            <ChevronRight className="h-3 w-3 text-gray-700 shrink-0" aria-hidden />
            <Link to={`/${c}`} className={`hover:text-gray-300 transition-colors truncate rounded ${FOCUS}`}>
              {catLabel(c)}
            </Link>
          </span>
        ))}
      </nav>

      {/* Category / sector line (§8) */}
      <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2">
        {cats.length > 0 && (
          <p className="font-mono text-[10.5px] font-bold uppercase tracking-[0.25em]" data-testid="article-category-line">
            <Link to={`/${cats[0]}`} className={`text-[#A78BFA] hover:text-[#00E5FF] transition-colors rounded ${FOCUS}`}>
              {catLabel(cats[0])}
            </Link>
            {cats[1] && (
              <>
                <span className="text-gray-700 mx-2" aria-hidden>|</span>
                <Link to={`/${cats[1]}`} className={`text-gray-500 hover:text-gray-300 transition-colors rounded ${FOCUS}`}>
                  {catLabel(cats[1])}
                </Link>
              </>
            )}
          </p>
        )}
        {liveBadge && (
          <span data-testid="article-breaking-label"
            className={`inline-flex items-center gap-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] rounded px-2 py-[3px] ${liveBadgeCls}`}>
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className={`motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${liveBadge === "developing" ? "bg-black" : "bg-white"}`} />
              <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${liveBadge === "developing" ? "bg-black" : "bg-white"}`} />
            </span>
            {liveBadge}
          </span>
        )}
        {isPr && (
          <span data-testid="article-pr-disclosure"
            className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-amber-300/90 border border-amber-500/30 bg-amber-500/[0.07] rounded px-2 py-[3px]">
            Press Release
          </span>
        )}
      </div>

      {/* Headline (§9) — strongest element on the page */}
      <h1 data-testid="article-title"
        className="mt-3.5 max-w-[880px] font-news font-black text-white tracking-tight text-[32px] leading-[1.14] sm:text-[40px] sm:leading-[1.1] lg:text-[46px] xl:text-[50px] lg:leading-[1.08]">
        {title}
      </h1>

      {/* Dek (§10) */}
      {dek && (
        <p data-testid="article-dek"
          className="mt-4 max-w-[720px] text-[16px] lg:text-[17px] text-gray-400 leading-relaxed">
          {dek}
        </p>
      )}

      {/* Author + metadata (§11) and actions (§12) */}
      <div className="mt-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="flex items-center gap-3 min-w-0" data-testid="article-meta">
          {article.author_name && !isExternal && (
            <AuroraAvatar name={article.author_name} className="h-10 w-10" textClass="text-xs" animate={false} />
          )}
          <div className="min-w-0">
            {article.author_name && !isExternal ? (
              <p className="flex items-center gap-1.5 text-[13.5px] font-bold text-white leading-tight">
                <Link to={`/author/${slugifyName(article.author_name)}`} data-testid="article-author-link"
                  className={`hover:text-[#00E5FF] transition-colors rounded ${FOCUS}`}>
                  {article.author_name}
                </Link>
                <BadgeCheck className="h-3.5 w-3.5 text-[#00E5FF] shrink-0" aria-label="NewsLayer author" />
              </p>
            ) : externalBold ? (
              <p className="text-[13.5px] font-bold text-gray-200 leading-tight" data-testid="article-source-line">
                {externalBold}
              </p>
            ) : null}
            <p className="font-mono text-[10.5px] text-gray-500 mt-0.5 truncate">
              {isExternal
                ? <span data-testid="article-publisher-line">
                    <span className="uppercase tracking-wider text-gray-600">Publisher</span>
                    {srcName && srcName.toLowerCase() !== externalBold.toLowerCase() ? ` ${srcName}` : ""}
                  </span>
                : role || "NewsLayer"}
            </p>
            <p className="font-mono text-[10.5px] text-gray-500 mt-0.5" data-testid="article-time-label">
              {fmtPublished(article.published_at)}
              {(showUpdated || article.read_minutes) && (
                <span className="text-gray-600">
                  {showUpdated && <> · Updated {timeAgo(updatedAt)}</>}
                  {article.read_minutes ? <> · {article.read_minutes} {t("minRead")}</> : null}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" data-testid="article-actions">
          {hasAudio && (
            <ActionBtn testid="article-listen-button" label="Listen to this article" onClick={onListen}>
              <Headphones className="h-3.5 w-3.5 text-[#00E5FF]" /> <span className="hidden sm:inline">Listen</span>
            </ActionBtn>
          )}
          <ActionBtn testid="article-save-button" label={saved ? t("saved") : t("save")} onClick={onSave}>
            {saved ? <BookmarkCheck className="h-3.5 w-3.5 text-[#00E5FF]" /> : <Bookmark className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">{saved ? t("saved") : t("save")}</span>
          </ActionBtn>
          <ActionBtn testid="article-share-button" label={t("share")} onClick={onShare}>
            <Share2 className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t("share")}</span>
          </ActionBtn>
          <ActionBtn testid="article-text-size-button"
            label={`Text size: ${TS_LABEL[textSize] || "M"} — tap to change`} onClick={onCycleTextSize}>
            <span className="font-serif text-[13px] leading-none">Aa</span>
            <span className="font-mono text-[9px] text-gray-500">{TS_LABEL[textSize] || "M"}</span>
          </ActionBtn>
        </div>
      </div>
    </header>
  );
};
